import { useState } from 'react';
import {
  createOperator,
  describeError,
  problemCode,
  type OperatorDto,
  type OperatorRole,
} from '@/lib/api';
import { SensitiveCredentialModal } from '@/components/SensitiveCredentialModal';

const ROLES: OperatorRole[] = ['SupportL1', 'SupportL2', 'Commercial', 'Admin'];

interface Props {
  onClose: () => void;
}

export function OperatorCreatePage({ onClose }: Props) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<OperatorRole>('SupportL1');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<OperatorDto | null>(null);
  // Held in component state ONLY; cleared on modal close (per UX contract).
  const [verificationCode, setVerificationCode] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const resp = await createOperator({ email, fullName, role });
      setCreated(resp.account);
      setVerificationCode(resp.verificationCode);
    } catch (err) {
      setError(describeFriendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  function handleConfirmShown() {
    // Per UX contract: drop the code from memory immediately.
    setVerificationCode(null);
    setCreated(null);
    onClose();
  }

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-operator-title"
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 32,
          maxWidth: 480,
          width: '90%',
          boxShadow: '0 24px 48px rgba(0,0,0,0.25)',
        }}
      >
        <h2 id="create-operator-title" style={{ marginTop: 0, fontSize: 20 }}>
          Criar operador
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="field">
            <label htmlFor="create-op-email">Email corporativo</label>
            <input
              id="create-op-email"
              type="email"
              autoComplete="off"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="create-op-fullname">Nome completo</label>
            <input
              id="create-op-fullname"
              type="text"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              maxLength={200}
            />
          </div>
          <div className="field">
            <label htmlFor="create-op-role">Papel</label>
            <select
              id="create-op-role"
              value={role}
              onChange={(e) => setRole(e.target.value as OperatorRole)}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          {error && <div className="error-banner">{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn"
              disabled={submitting}
              style={{ background: 'var(--primary, #F97316)', color: '#fff', padding: '8px 16px', borderRadius: 8 }}
            >
              {submitting ? 'Criando…' : 'Criar e gerar convite'}
            </button>
          </div>
        </form>
      </div>

      {created && verificationCode && (
        <SensitiveCredentialModal
          verificationCode={verificationCode}
          operatorEmail={created.email}
          operatorFullName={created.fullName}
          contextLabel="Convite enviado"
          onConfirm={handleConfirmShown}
        />
      )}
    </div>
  );
}

function describeFriendlyError(err: unknown): string {
  const code = problemCode(err);
  if (code === 'platform.accounts.email_in_use') {
    return 'Este email já está em uso por um operador ativo. Para reativar, primeiro revogue a conta existente.';
  }
  if (code === 'platform.accounts.last_admin') {
    return 'Não é possível remover o último admin ativo.';
  }
  if (code === 'platform.concurrency.exhausted') {
    return 'Sistema ocupado, tente novamente em alguns segundos.';
  }
  return describeError(err);
}
